import type { GazeTrack } from '../../../services/emotionAnalysisService'

/**
 * Gaze heat map over the stimulus player (eye-tracking studies, Z-118).
 *
 * The recipe is Miłosz's `attention_map` (eye-tracking-sandbox,
 * `gaze/eval/fixations.py`) tuned by Paweł in the heat-map lab on 2026-10-21:
 * fixations instead of raw samples (a saccade never paints the picture), each
 * weighted by how long it overlaps the window, a wide Gaussian, a linear sum,
 * one scale for the whole segment, and a steep curve so a place turns red as
 * soon as a few people settle on it.
 */

/** Visual angle the stimulus spans: a 1280 px picture on a 1920 px, 34.5 cm
 *  screen seen from 60 cm. Turns the degree settings below into picture widths. */
export const STIMULUS_DEG = 22
/** The look of the map — every number Paweł tunes in the heat-map lab, which
 *  runs this very file. */
export interface HeatParams {
  /** I-DT: the most the gaze may wander (width + height) and still be one fixation. */
  fixDispersionDeg: number
  /** I-DT: the shortest run that counts as a fixation. */
  fixMinMs: number
  /** "Moment" mode: how far behind the playhead the map looks… */
  momentMs: number
  /** …and how fast older looks fade (exponential time constant). */
  decayMs: number
  /** Blur of one fixation, as a Gaussian sigma. */
  sigmaDeg: number
  /** How fast a place turns red: v → (1 - e^(-kv)) / (1 - e^(-k)). */
  speed: number
  /** Below this share of the scale nothing is drawn; the opacity then ramps in
   *  over `feather` of the scale. */
  cutoff: number
  feather: number
  /** A lone glance — one respondent of a group, for a moment — stays well under
   *  the scale's top but the fast red curve still blows it up into a big blob
   *  that blinks in and out. Raw density below `rawFloor` is not drawn and fades
   *  in up to 3× that, before the curve is applied. */
  rawFloor: number
  /** Opacity of the hottest place. */
  opacity: number
}

export const HEAT_PARAMS: HeatParams = {
  fixDispersionDeg: 1.5,
  fixMinMs: 100,
  momentMs: 2000,
  decayMs: 800,
  sigmaDeg: 0.45,
  speed: 5,
  cutoff: 0.06,
  feather: 0.3,
  rawFloor: 0.025,
  opacity: 0.7,
}

/** A gap longer than this many sampling steps (blink, lost face) splits a run. */
const GAP_FACTOR = 3
/** How often the segment's peak is sampled for the shared scale. */
const PEAK_STEP_MS = 250

/** Density grid, 16:9 like the stage, so one grid pixel is square on screen. */
export const GRID_W = 160
export const GRID_H = 90

export interface Fixation {
  ts: number
  te: number
  x: number
  y: number
}

/**
 * Fixations in `track` between `from` and `to` (ms), found per respondent with
 * the dispersion-threshold algorithm (I-DT). `track.r` says whose each point
 * is; a track without it (one person's own gaze) is a single respondent.
 */
export function detectFixations(track: GazeTrack, from: number, to: number, p: HeatParams = HEAT_PARAMS): Fixation[] {
  const byR = new Map<number, number[]>()
  for (let i = 0; i < track.t_ms.length; i++) {
    const t = track.t_ms[i]
    if (t < from || t >= to || track.x[i] == null || track.y[i] == null) continue
    const r = track.r?.[i] ?? 0
    let list = byR.get(r)
    if (!list) byR.set(r, (list = []))
    list.push(i)
  }
  const thr = p.fixDispersionDeg / STIMULUS_DEG
  const aspect = GRID_H / GRID_W // y is in stage heights; dispersion is in widths
  const out: Fixation[] = []
  for (const idx of byR.values()) {
    idx.sort((a, b) => track.t_ms[a] - track.t_ms[b])
    const t = idx.map((i) => track.t_ms[i])
    const x = idx.map((i) => track.x[i] as number)
    const y = idx.map((i) => (track.y[i] as number) * aspect)
    if (t.length < 2) continue
    const steps = t.slice(1).map((v, k) => v - t[k]).sort((a, b) => a - b)
    const dt = steps[steps.length >> 1] || 50
    const maxGap = GAP_FACTOR * dt
    const dispersion = (i: number, j: number) => {
      let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity
      for (let k = i; k < j; k++) {
        if (x[k] < x0) x0 = x[k]
        if (x[k] > x1) x1 = x[k]
        if (y[k] < y0) y0 = y[k]
        if (y[k] > y1) y1 = y[k]
      }
      return x1 - x0 + (y1 - y0)
    }
    let lo = 0
    for (let h = 1; h <= t.length; h++) {
      if (h < t.length && t[h] - t[h - 1] <= maxGap) continue
      let i = lo
      while (i < h) {
        let j = i + 1
        while (j < h && t[j - 1] - t[i] < p.fixMinMs) j++
        if (t[j - 1] - t[i] < p.fixMinMs) break
        if (dispersion(i, j) <= thr) {
          while (j < h && dispersion(i, j + 1) <= thr) j++
          let sx = 0, sy = 0
          for (let k = i; k < j; k++) {
            sx += x[k]
            sy += y[k]
          }
          out.push({ ts: t[i], te: t[j - 1] + dt, x: sx / (j - i), y: sy / (j - i) / aspect })
          i = j
        } else i++
      }
      lo = h
    }
  }
  return out.sort((a, b) => a.ts - b.ts)
}

/** Box widths whose three passes approximate a Gaussian of `sigma`. */
function boxesForGauss(sigma: number): number[] {
  const ideal = Math.sqrt((12 * sigma * sigma) / 3 + 1)
  let wl = Math.floor(ideal)
  if (wl % 2 === 0) wl--
  const wu = wl + 2
  const m = Math.round((12 * sigma * sigma - 3 * wl * wl - 12 * wl - 9) / (-4 * wl - 4))
  return [0, 1, 2].map((i) => (i < m ? wl : wu))
}

function boxPass(src: Float32Array, dst: Float32Array, r: number, horizontal: boolean) {
  const [n, lines] = horizontal ? [GRID_W, GRID_H] : [GRID_H, GRID_W]
  const at = (line: number, k: number) => (horizontal ? line * GRID_W + k : k * GRID_W + line)
  const norm = 1 / (2 * r + 1)
  for (let line = 0; line < lines; line++) {
    // zero outside the picture, like the sandbox's blur on a bounded screen
    let acc = 0
    for (let k = 0; k <= Math.min(r, n - 1); k++) acc += src[at(line, k)]
    for (let k = 0; k < n; k++) {
      dst[at(line, k)] = acc * norm
      const add = k + r + 1
      const drop = k - r
      if (add < n) acc += src[at(line, add)]
      if (drop >= 0) acc -= src[at(line, drop)]
    }
  }
}

function gaussianBlur(grid: Float32Array, sigma: number): Float32Array {
  let a = grid
  let b = new Float32Array(grid.length)
  for (const w of boxesForGauss(sigma)) {
    const r = (w - 1) / 2
    boxPass(a, b, r, true)
    ;[a, b] = [b, a]
    boxPass(a, b, r, false)
    ;[a, b] = [b, a]
  }
  return a
}

/**
 * Attention density at `tau`. `moment`: fixations overlapping the
 * `momentMs` before the playhead (never before `segStart`), each weighted
 * by its overlap and faded by its age. Otherwise the whole `[segStart, segEnd)`.
 */
export function heatDensity(
  fixations: Fixation[],
  mode: 'moment' | 'whole',
  tau: number,
  segStart: number,
  segEnd: number,
  p: HeatParams = HEAT_PARAMS,
): Float32Array {
  const [t0, t1] = mode === 'whole' ? [segStart, segEnd] : [Math.max(segStart, tau - p.momentMs), tau]
  const grid = new Float32Array(GRID_W * GRID_H)
  for (const f of fixations) {
    if (f.ts >= t1) break
    const a = Math.max(f.ts, t0)
    const b = Math.min(f.te, t1)
    if (b <= a) continue
    const w = (b - a) * (mode === 'moment' ? Math.exp(-(tau - (a + b) / 2) / p.decayMs) : 1)
    // bilinear splat, so the blob centre is sub-pixel
    const gx = f.x * GRID_W - 0.5
    const gy = f.y * GRID_H - 0.5
    const ix = Math.floor(gx)
    const iy = Math.floor(gy)
    const fx = gx - ix
    const fy = gy - iy
    const put = (xx: number, yy: number, k: number) => {
      if (xx >= 0 && xx < GRID_W && yy >= 0 && yy < GRID_H) grid[yy * GRID_W + xx] += w * k
    }
    put(ix, iy, (1 - fx) * (1 - fy))
    put(ix + 1, iy, fx * (1 - fy))
    put(ix, iy + 1, (1 - fx) * fy)
    put(ix + 1, iy + 1, fx * fy)
  }
  return gaussianBlur(grid, (p.sigmaDeg / STIMULUS_DEG) * GRID_W)
}

function maxOf(grid: Float32Array): number {
  let m = 0
  for (let i = 0; i < grid.length; i++) if (grid[i] > m) m = grid[i]
  return m
}

/** The segment's hottest moment: one scale for the whole clip, so a frame where
 *  few people look stays faint instead of glowing like the group's focus. */
export function segmentPeak(
  fixations: Fixation[],
  mode: 'moment' | 'whole',
  segStart: number,
  segEnd: number,
  p: HeatParams = HEAT_PARAMS,
): number {
  if (mode === 'whole') return maxOf(heatDensity(fixations, 'whole', segEnd, segStart, segEnd, p))
  let peak = 0
  for (let tau = segStart + PEAK_STEP_MS; tau <= segEnd; tau += PEAK_STEP_MS) {
    peak = Math.max(peak, maxOf(heatDensity(fixations, 'moment', tau, segStart, segEnd, p)))
  }
  return peak
}

/** Colour ramp: blue where few looked, red where most did. */
const STOPS: Array<[number, [number, number, number]]> = [
  [0, [40, 90, 255]],
  [0.25, [25, 211, 211]],
  [0.5, [60, 210, 60]],
  [0.75, [255, 225, 0]],
  [1, [255, 42, 0]],
]

let lut: Uint8ClampedArray | null = null
let lutKey = ''
function palette(p: HeatParams): Uint8ClampedArray {
  const key = `${p.cutoff}:${p.feather}`
  if (lut && lutKey === key) return lut
  lutKey = key
  const out = new Uint8ClampedArray(256 * 4)
  for (let i = 0; i < 256; i++) {
    const v = i / 255
    let j = 1
    while (j < STOPS.length - 1 && STOPS[j][0] < v) j++
    const [p0, c0] = STOPS[j - 1]
    const [p1, c1] = STOPS[j]
    const f = Math.min(1, Math.max(0, (v - p0) / (p1 - p0)))
    for (let k = 0; k < 3; k++) out[i * 4 + k] = c0[k] + (c1[k] - c0[k]) * f
    // opacity: nothing below the cutoff, smoothstep up to full over the feather
    const a = p.feather > 0 ? Math.min(1, Math.max(0, (v - p.cutoff) / p.feather)) : v >= p.cutoff ? 1 : 0
    out[i * 4 + 3] = Math.round(a * a * (3 - 2 * a) * 255)
  }
  return (lut = out)
}

/** Paints `density` (scaled by `peak`) into a GRID_W × GRID_H image. */
export function paintHeat(density: Float32Array, peak: number, img: ImageData, p: HeatParams = HEAT_PARAMS) {
  const px = img.data
  px.fill(0)
  if (peak <= 0) return
  const L = palette(p)
  const k = 1 - Math.exp(-p.speed)
  for (let i = 0; i < density.length; i++) {
    const raw = Math.min(1, density[i] / peak)
    if (raw <= 0) continue
    const v = p.speed > 0 ? (1 - Math.exp(-p.speed * raw)) / k : raw
    const li = Math.round(v * 255) * 4
    px[i * 4] = L[li]
    px[i * 4 + 1] = L[li + 1]
    px[i * 4 + 2] = L[li + 2]
    const g = p.rawFloor > 0 ? Math.min(1, Math.max(0, (raw - p.rawFloor) / (2 * p.rawFloor))) : 1
    px[i * 4 + 3] = Math.round(L[li + 3] * g * g * (3 - 2 * g) * p.opacity)
  }
}
